<template>
  <div class="assistant-page">
    <section class="assistant-shell">
      <header class="assistant-head">
        <div class="head-icon">🤖</div>
        <div><h3>AI 合规助手</h3><p>7×24 在线 · 合规问题实时解答</p></div>
      </header>
      <div ref="chat" class="chat-area">
        <div class="message ai-message welcome-message">
          <div class="avatar ai-avatar">🤖</div>
          <div class="bubble welcome-bubble">
            <p>您好！我是 AI 合规助手，可解答《医药代表管理办法》相关问题、查询合规规则、分析行为风险。您可以问我：</p>
            <p>• 新办法对代表推广有什么禁止行为？<br>• 张伟最近的拜访有什么风险？<br>• 集采政策对我们的推广有什么影响？</p>
          </div>
        </div>
        <template v-for="item in qaList">
          <div :key="'q-' + item.id" class="message user-message">
            <div class="avatar user-avatar">👤</div><div class="bubble user-bubble">{{ item.question }}</div>
          </div>
          <div :key="'a-' + item.id" class="message ai-message">
            <div class="avatar ai-avatar">🤖</div>
            <div class="bubble answer-bubble" :class="[statusClass(item.status), { failed: item.error }]">
              <span v-if="item.intent" class="intent-tag">{{ intentLabel(item.intent) }}</span>
              <template v-if="item.loading"><div class="loading-copy"><i class="el-icon-loading"></i> AI 正在分析...</div></template>
              <template v-else-if="item.error">
                <h4>AI 智能解答</h4><p>AI 服务暂时不可用，请稍后重试。</p>
                <el-button size="mini" plain :disabled="sending" @click="retry(item)">重新尝试</el-button>
              </template>
              <template v-else>
                <h4>{{ answerTitle(item.intent) }}：{{ statusLabel(item.status) }}</h4>
                <div v-if="statusHint(item.status)" class="status-hint">{{ statusHint(item.status) }}</div>
                <p>{{ item.answer }}</p>
              </template>
            </div>
          </div>
        </template>
      </div>
      <footer class="composer">
        <el-input v-model="question" type="textarea" :rows="2" resize="none" :disabled="sending" placeholder="输入问题，如：张伟最近的拜访有什么风险？" @keydown.native="handleKeydown" />
        <el-button class="send-button" type="primary" :loading="sending" :disabled="!question.trim()" @click="send"><i v-if="!sending" class="el-icon-position"></i> 发送</el-button>
      </footer>
      <div class="assistant-note">💡 AI 回答基于合规知识库，关键操作需人工确认</div>
    </section>
  </div>
</template>

<script>
import { aiApi } from '../services/api'

export default {
  data: () => ({ question: '', sending: false, qaList: [], nextId: 1 }),
  created () { this.resetSession() },
  beforeDestroy () { this.resetSession() },
  methods: {
    resetSession () { this.question = ''; this.sending = false; this.qaList = []; this.nextId = 1 },
    intentLabel (intent) {
      const labels = {
        phrase_review: '话术审查', adverse_event: '不良反应/不良事件处理',
        prohibited_benefit: '利益输送风险', sales_redline: '销售红线风险',
        filing_authorization: '备案/授权/登记问题', privacy: '患者隐私问题',
        general_compliance: '一般合规问题'
      }
      return labels[String(intent || '').toLowerCase()] || '其他合规问题'
    },
    answerTitle (intent) {
      const titles = {
        phrase_review: 'AI 话术审查', adverse_event: 'AI 不良事件处理指引',
        prohibited_benefit: 'AI 利益输送风险判断', sales_redline: 'AI 销售红线判断',
        filing_authorization: 'AI 备案授权判断', privacy: 'AI 隐私合规判断',
        general_compliance: 'AI 合规解答'
      }
      return titles[String(intent || '').toLowerCase()] || 'AI 智能解答'
    },
    statusLabel (status) {
      const labels = { pass: '合规', warn: '合规提醒', review: '待人工复核', block: '高风险，已拦截', fail: '不合规！' }
      return labels[String(status || '').toLowerCase()] || '待确认'
    },
    statusHint (status) {
      const hints = { warn: '建议注意措辞后再使用。', review: '存在不确定风险，请确认或提交人工复核。', block: '已命中高风险规则，请修改后再使用。' }
      return hints[String(status || '').toLowerCase()] || ''
    },
    statusClass (status) { return 'status-' + String(status || 'unknown').toLowerCase() },
    handleKeydown (event) {
      if (event.isComposing) return
      if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); this.send() }
    },
    async send () {
      const value = (this.question || '').trim()
      if (!value || this.sending) return
      const item = { id: this.nextId++, requestId: '', question: value, intent: '', status: '', answer: '', loading: true, error: false }
      this.qaList.push(item); this.question = ''; this.scrollToBottom(); await this.ask(item)
    },
    async retry (item) {
      if (this.sending) return
      item.requestId = ''; item.intent = ''; item.status = ''; item.answer = ''; item.loading = true; item.error = false
      this.scrollToBottom(); await this.ask(item)
    },
    async ask (item) {
      this.sending = true
      try {
        const result = await aiApi.askComplianceAssistant(item.question)
        item.requestId = result.request_id || ''; item.intent = result.intent || ''; item.status = result.status || ''; item.answer = result.answer || ''
      } catch (e) { item.error = true } finally { item.loading = false; this.sending = false; this.scrollToBottom() }
    },
    scrollToBottom () {
      this.$nextTick(() => { const chat = this.$refs.chat; if (chat) chat.scrollTop = chat.scrollHeight })
    }
  }
}
</script>

<style scoped>
.assistant-page{padding:22px 26px 36px;height:calc(100vh - 68px);background:#f3f6f8}.assistant-shell{height:100%;min-height:560px;background:#fff;border:1px solid #e0e6ed;border-radius:14px;box-shadow:0 5px 20px rgba(27,55,78,.05);display:flex;flex-direction:column;overflow:hidden}.assistant-head{height:78px;flex:none;border-bottom:1px solid #e5eaf0;display:flex;align-items:center;padding:0 25px;gap:13px}.head-icon{width:44px;height:44px;border-radius:50%;background:#f0eaff;display:flex;align-items:center;justify-content:center;font-size:23px}.assistant-head h3{margin:0 0 5px;font-size:18px}.assistant-head p{margin:0;color:#8a96a3;font-size:12px}.chat-area{flex:1;overflow-y:auto;margin:18px 22px 0;padding:23px;background:#f6f8fa;border:1px solid #e2e7ed;border-radius:16px;scroll-behavior:smooth}.message{display:flex;gap:12px;align-items:flex-start;margin-bottom:22px}.user-message{justify-content:flex-end}.user-message .avatar{order:2}.avatar{width:38px;height:38px;border-radius:50%;display:flex;align-items:center;justify-content:center;flex:none}.ai-avatar{background:#7656ce;color:#fff}.user-avatar{background:#1688c5;color:#fff}.bubble{max-width:76%;border-radius:13px;padding:15px 18px;line-height:1.7;font-size:14px;white-space:pre-wrap;word-break:break-word}.welcome-bubble,.user-bubble{background:#fff;border:1px solid #e3e8ee}.welcome-bubble p{margin:0 0 9px}.welcome-bubble p:last-child{margin:0;color:#657381}.answer-bubble{position:relative;background:#fff;border:1px solid #dedfe7;border-left:5px solid #7656ce;padding-top:18px}.answer-bubble.status-pass{background:#f3fbf6;border-left-color:#29945d}.answer-bubble.status-warn{background:#fff9eb;border-left-color:#dc941e}.answer-bubble.status-review{background:#f7f3ff;border-left-color:#7b52d2}.answer-bubble.status-block,.answer-bubble.status-fail{background:#fff2f2;border-left-color:#d74444}.answer-bubble.failed{border-left-color:#d74444}.intent-tag{display:inline-block;color:#6b4cc1;background:#f0eaff;border-radius:11px;padding:2px 9px;font-size:11px;margin-bottom:11px}.answer-bubble h4{margin:0 0 8px;font-size:15px;color:#303746}.status-pass h4{color:#218454}.status-warn h4{color:#a96c0b}.status-block h4,.status-fail h4,.failed h4{color:#b64242}.status-hint{margin:0 0 10px;padding:7px 10px;border-radius:7px;background:rgba(255,255,255,.75);color:#6c7480;font-size:12px}.answer-bubble p{margin:0;color:#566170}.loading-copy{color:#7457c8}.composer{display:flex;gap:14px;padding:17px 22px 12px;align-items:stretch;flex:none}.composer .el-textarea{flex:1}.send-button{width:116px;background:#7657cf;border-color:#7657cf;border-radius:9px}.assistant-note{text-align:center;color:#8995a1;font-size:12px;padding:0 20px 16px;flex:none}
</style>
